import { getBasename, removeBasename, getValidBasenames } from './history';
/**
 * Returns the current pathname with basename removed
 * For HCSC brands: /mentalhealthhub/topics/stress -> /topics/stress
 * For other brands: /topics/stress -> /topics/stress
 */
export const getCleanPathname = () => {
  let { pathname } = window.location;
  const basename = getBasename();

  if (basename) {
    pathname = removeBasename(pathname);
    if (!pathname || pathname === '') {
      pathname = '/';
    }
  } else {
    // Fallback: if brand detection fails but the URL contains a known basename,
    // strip it. This handles cases where getSubdomain()/getLocalData('brand')
    // doesn't match a PATH_PREFIX key but the URL still has a basename prefix.
    const validBasenames = getValidBasenames();
    const matchedBasename = validBasenames.find(b => pathname.startsWith(b));
    if (matchedBasename) {
      pathname = pathname.slice(matchedBasename.length);
      if (!pathname || !pathname.startsWith('/')) {
        pathname = `/${pathname || ''}`;
      }
    }
  }

  return pathname;
};

/**
 * Returns clean pathname segments (without basename)
 * /mentalhealthhub/topics/stress -> ['topics', 'stress']
 * /topics/stress -> ['topics', 'stress']
 * / -> []
 */
export const getCleanPathSegments = () =>
  getCleanPathname()
    .split('/')
    .filter(Boolean);

/**
 * Returns the path segment (first segment after basename removal)
 * This is the "category" or route like 'topics', 'podcasts', 'assessments'
 *
 * /mentalhealthhub/topics/stress -> 'topics'
 * /podcasts/soothe-stress -> 'podcasts'
 * / -> undefined
 */
export const getPathSegment = () => getCleanPathSegments()[0];

/**
 * Returns the slug segment (second segment after basename removal)
 *
 * /mentalhealthhub/topics/stress -> 'stress'
 * /podcasts/soothe-stress -> 'soothe-stress'
 * /topics -> undefined
 */
export const getSlugSegment = () => getCleanPathSegments()[1];

/**
 * Extracts the page name from the current URL for analytics tracking
 * Handles HCSC basename-prefixed URLs correctly
 *
 * /topics/stress -> 'topicsPage'
 * /mentalhealthhub/topics/stress -> 'topicsPage'
 * /insights -> 'insightsPage'
 * /assessments/anxiety/results -> 'assessmentResultsPage'
 * /learning-lab#meditation -> 'meditationPage'
 * / -> 'homePage'
 */
export const getEventPageName = () => {
  const pathname = getCleanPathname();
  const segments = getCleanPathSegments();

  if (segments.length === 0) {
    return 'homePage';
  }

  const firstSegment = segments[0]?.toLowerCase();

  // Special case: learning-lab uses hash as page name
  if (/learning-lab/.test(pathname)) {
    const hashSegment = window.location.hash.slice(1).split('?')[0];
    if (hashSegment) {
      return `${hashSegment}Page`;
    }
  }

  // Special case: blogs maps to insights
  if (['insights', 'blogs'].includes(firstSegment)) {
    return 'insightsPage';
  }

  // Special case: assessment results
  if (
    firstSegment === 'assessments' &&
    segments.length >= 3 &&
    segments[2] === 'results'
  ) {
    return 'assessmentResultsPage';
  }

  return `${firstSegment}Page`;
};
